// @ts-nocheck

import React, { useMemo, useState } from 'react';
import { Grid2, Typography, Card, CardContent, Box, Avatar, Stack, Skeleton, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import OrgDialog from '@/components/organisms/Dialog';
import { useDispatch, useSelector } from 'react-redux';
import AtomLineChart from '@/components/atoms/LineChart';
import OrgDataTable from '@/components/organisms/DataTable';
import { AppDispatch } from '@/store';
import {
  selectCurrentCompany,
  selectCompanyContacts,
  selectCompanyStudies,
  selectCompaniesLoading,
  companies,
} from '@/store/companies';
import { briefings } from '@/store/briefings/thunks';
import { financials, selectFinancials } from '@/store/financials';
import { selectBriefingsList, selectBriefingsLoading } from '@/store/briefings/selectors';
import { selectStudiesLoading } from '@/store/studies/selectors';

interface ChartDataPoint {
  x: any;
  y: number;
}
function transformToChartData(data: any[]): ChartDataPoint[] {
  const quarterToMonth: Record<string, number> = { 1: 0, 2: 3, 3: 6, 4: 9 };
  if (!data || data.length === 0) return [];
  return data
    .map(point => ({ x: new Date(point.year, quarterToMonth[point.quarter], 1), y: point.revenue }))
    .sort((a, b) => a.x.getTime() - b.x.getTime());
}

function formatMoney(value: number | null | undefined, currency = 'CHF') {
  if (value === null || value === undefined || Number.isNaN(value)) return 'N/A';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
  } catch (e) {
    return `${value} ${currency}`;
  }
}

const SupplierDashboard: React.FC = () => {
  // Selectors
  const currentCompany = useSelector(selectCurrentCompany);
  const contacts = useSelector(selectCompanyContacts) || [];
  const financialsData = useSelector(selectFinancials) || { financialsData: [] };
  const companyStudies = useSelector(selectCompanyStudies) || [];
  const briefingsData = useSelector(selectBriefingsList) || [];

  const companiesLoading = useSelector(selectCompaniesLoading);
  const studiesLoading = useSelector(selectStudiesLoading);
  const briefingsLoading = useSelector(selectBriefingsLoading);

  const dispatch = useDispatch<AppDispatch>();

  // State for pagination
  const [studiesPage, setStudiesPage] = React.useState(0);
  const [studiesRowsPerPage, setStudiesRowsPerPage] = React.useState(10);

  const [briefingsPage, setBriefingsPage] = React.useState(1);
  const [briefingsRowsPerPage, setBriefingsRowsPerPage] = React.useState(10);

  // Contacts dialog state
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({ first_name: '', last_name: '', email: '', department: '', title: '' });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [contactDeleteId, setContactDeleteId] = useState<string | null>(null);

  // Columns
  const contactColumns = useMemo(
    () => [
      { id: 'first_name', label: 'First Name', flex: 1 },
      { id: 'last_name', label: 'Last Name', flex: 1 },
      { id: 'email', label: 'Email', flex: 1 },
      { id: 'department', label: 'Department', flex: 1 },
      { id: 'title', label: 'Title', flex: 1 },
    ],
    []
  );

  const studyColumns = useMemo(
    () => [
      { id: 'study_name', label: 'Study Name', flex: 2 },
      { id: 'year', label: 'Year', flex: 1 },
      { id: 'revenue', label: 'Revenue', flex: 1 },
      { id: 'status', label: 'Status', flex: 1 },
      { id: 'units_sold', label: 'Units Sold', flex: 1 },
      { id: 'selling_price', label: 'Selling Price', flex: 1 },
      { id: 'yoy_revenue_percentage', label: 'YoY Revenue %', flex: 1 }
    ],
    []
  );

  const briefingColumns = useMemo(
    () => [
      { id: 'subject', label: 'Subject', flex: 2 },
      { id: 'date', label: 'Date', flex: 1 },
      { id: 'start_time', label: 'Start', flex: 1 },
      { id: 'end_time', label: 'End', flex: 1 },
      { id: 'owner', label: 'Owner', flex: 1 },
      { id: 'notes', label: 'Notes', flex: 2 },
    ],
    []
  );

  const lineChartSeries = useMemo(() => {
    if (!financialsData || (financialsData.financialsData || []).length === 0) {
      return { id: 'financials', label: 'Revenues', color: '#2196f3', data: [] };
    }
    return { id: 'financials', label: 'Revenues', color: '#2196f3', data: transformToChartData(financialsData.financialsData) };
  }, [financialsData]);

  const studiesWithId = useMemo(() => companyStudies.map(s => ({ ...s, id: s.study_year_id || s.id })), [companyStudies]);

  const paginatedStudies = useMemo(() => {
    const startIndex = studiesPage * studiesRowsPerPage;
    return studiesWithId.slice(startIndex, startIndex + studiesRowsPerPage);
  }, [studiesWithId, studiesPage, studiesRowsPerPage]);

  const briefingRows = useMemo(() => (briefingsData || []).map(b => ({
    id: b.id,
    subject: b.subject || b.title || '',
    date: b.start_time ? new Date(b.start_time).toLocaleDateString() : (b.date ? new Date(b.date).toLocaleDateString() : ''),
    start_time: b.start_time ? new Date(b.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
    end_time: b.end_time ? new Date(b.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
    owner: `${b.owner?.first_name || ''} ${b.owner?.last_name || ''}`.trim(),
    notes: b.notes || b.description || '',
  })), [briefingsData]);

  // Handlers
  const handleStudiesPageChange = (newPage: number) => setStudiesPage(newPage);
  const handleStudiesRowsPerPageChange = (newRowsPerPage: number) => { setStudiesRowsPerPage(newRowsPerPage); setStudiesPage(0); };

  const handleBriefingsPageChange = (newPage: number) => {
    setBriefingsPage(newPage);
    if (briefings && typeof briefings.getBriefings === 'function' && currentCompany) {
      dispatch(briefings.getBriefings({ company_id__eq: currentCompany.id, page: newPage, page_size: briefingsRowsPerPage }));
    }
  };
  const handleBriefingsRowsPerPageChange = (newRowsPerPage: number) => {
    setBriefingsRowsPerPage(newRowsPerPage);
    setBriefingsPage(1);
    if (briefings && typeof briefings.getBriefings === 'function' && currentCompany) {
      dispatch(briefings.getBriefings({ company_id__eq: currentCompany.id, page: 1, page_size: newRowsPerPage }));
    }
  };

  // Initial data load when company selected
  React.useEffect(() => {
    if (!currentCompany) return;
    dispatch(companies.getCompanyContacts(currentCompany.id));
    dispatch(financials.getFinancials({ company_id__eq: currentCompany.id }));
    dispatch(companies.getCompanyStudies(currentCompany.id));
    if (briefings && typeof briefings.getBriefings === 'function') {
      dispatch(briefings.getBriefings({ company_id__eq: currentCompany.id, page: briefingsPage, page_size: briefingsRowsPerPage }));
    }
  }, [currentCompany]);

  const openAddContact = () => {
    setEditingContact(null);
    setContactForm({ first_name: '', last_name: '', email: '', department: '', title: '' });
    setContactDialogOpen(true);
  };

  const openEditContact = (row: any) => {
    setEditingContact(row);
    setContactForm({ first_name: row.first_name || '', last_name: row.last_name || '', email: row.email || '', department: row.department || '', title: row.title || '' });
    setContactDialogOpen(true);
  };

  const handleContactFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setContactForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveContact = async () => {
    try {
      if (!currentCompany) return;
      if (editingContact) {
        // Update
        await dispatch(companies.updateCompanyContact({ companyId: currentCompany.id, contactId: editingContact.id, contactData: contactForm }));
      } else {
        // Create
        await dispatch(companies.createCompanyContact({ companyId: currentCompany.id, contactData: contactForm }));
      }

      // Refresh
      await dispatch(companies.getCompanyContacts(currentCompany.id));
      setContactDialogOpen(false);
      setEditingContact(null);
      setContactForm({ first_name: '', last_name: '', email: '', department: '', title: '' });
    } catch (error) {
      console.error('Contact save error', error);
    }
  };

  const openDeleteConfirm = (contactId: string) => {
    setContactDeleteId(contactId);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!currentCompany || !contactDeleteId) return;
    try {
      await dispatch(companies.deleteCompanyContact({ companyId: currentCompany.id, contactId: contactDeleteId }));
      await dispatch(companies.getCompanyContacts(currentCompany.id));
      setDeleteOpen(false);
      setContactDeleteId(null);
    } catch (error) {
      console.error('Delete contact error', error);
    }
  };

  if (!currentCompany) return <div>No company selected</div>;

  const anyLoading = companiesLoading || studiesLoading || briefingsLoading;

  return (
    <div>
      <Typography variant="h4" color="primary" fontWeight="bold" gutterBottom>
        {currentCompany.name} Dashboard
      </Typography>

      <Grid2 container spacing={2} alignItems="stretch" sx={{ flexDirection: 'column', p: { xs: 2, md: 4 } }}>
        {/* Top: left overview, right financials */}
        
        <Grid2 xs={12} md={4} sx={{ display: 'flex' }}>
          <Card elevation={6} sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems:'center',}}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ width: 64, height: 64, bgcolor: (t) => t.palette.primary.main, fontSize: 24 }}>
                  {currentCompany.company_name ? currentCompany.company_name.charAt(0) : currentCompany.name?.charAt(0) || 'C'}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" color="primary" fontWeight={700} noWrap>
                    {currentCompany.company_name || currentCompany.name || 'Company'}
                  </Typography>
                 
                </Box>
              </Stack>

                  <Box>
                  <Typography variant="caption" color="text.secondary">Studies</Typography>
                  <Typography variant="h5" fontWeight={700}>{currentCompany.number_of_studies ?? companyStudies.length}</Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">Region</Typography>
                  <Typography variant="subtitle1">{currentCompany.region_headquartered || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Currency</Typography>
                  <Typography variant="subtitle1">{currentCompany.currency || 'N/A'}</Typography>
                </Box>

              <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary">Latest Revenue</Typography>
                  <Typography variant="subtitle1">{formatMoney(currentCompany.latest_revenue, currentCompany.currency || 'CHF')}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Financials rev: {formatMoney(currentCompany.latest_financials_revenue, currentCompany.currency || 'CHF')}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid2>

        <Grid2 xs={12} md={8} sx={{ display: 'flex' }}>
          <Card elevation={3} sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <Typography variant="h6">Financials</Typography>
              {anyLoading ? (
                <Skeleton variant="rectangular" height={280} sx={{ mt: 1 }} />
              ) : (
                <Box sx={{ width: '100%', height: { xs: 240, md: 'auto' }, mt: 1 }}>
                  <AtomLineChart
                    series={[lineChartSeries]}
                    showHorizontalGrid
                    xLabel="Quarter"
                    showTooltip
                    showLegend
                    formatAsQuarters
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid2>

        {/* Studies */}
        <Grid2 xs={12}>
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h5">Studies</Typography>
              {studiesLoading ? (
                <Skeleton variant="rectangular" height={200} sx={{ mt: 1 }} />
              ) : (
                <Box sx={{ mt: 1 }}>
                  <OrgDataTable
                    columns={studyColumns}
                    data={paginatedStudies}
                    page={studiesPage}
                    rowsPerPage={studiesRowsPerPage}
                    totalRows={companyStudies.length}
                    onPageChange={handleStudiesPageChange}
                    onRowsPerPageChange={handleStudiesRowsPerPageChange}
                    sortBy="revenue"
                    sortDirection="desc"
                    disableColumnMenu
                    disableColumnResize
                    enableZebraStripes
                    hideFooter={false}
                    sx={{ mt: 2 }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid2>

        {/* Contacts */}
        <Grid2 xs={12}>
          <Card sx={{ mt: 2 }}>
            <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="h5">Contacts</Typography>
                  <Button variant="contained" startIcon={<Add />} onClick={openAddContact}>Add Contact</Button>
                </Stack>
              {companiesLoading ? (
                <Skeleton variant="rectangular" height={160} sx={{ mt: 1 }} />
              ) : (
                <Box sx={{ mt: 1 }}>
                  <OrgDataTable
                    columns={contactColumns}
                    data={contacts}
                    page={1}
                    rowsPerPage={10}
                    totalRows={contacts.length}
                    onPageChange={() => { }}
                    onRowsPerPageChange={() => { }}
                    sortBy="first_name"
                    sortDirection="asc"
                    disableColumnMenu
                    disableColumnResize
                    enableZebraStripes
                    hideFooter={false}
                    renderActions={row => (
                      <Stack direction="row">
                        <IconButton size="small" onClick={() => openEditContact(row)}><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => openDeleteConfirm(row.id)}><Delete fontSize="small" /></IconButton>
                      </Stack>
                    )}
                    sx={{ mt: 2 }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid2>

        {/* Briefings */}
        <Grid2 xs={12}>
          <Card sx={{ mt: 2, mb: 4 }}>
            <CardContent>
              <Typography variant="h5">Briefings</Typography>
              {briefingsLoading ? (
                <Skeleton variant="rectangular" height={160} sx={{ mt: 1 }} />
              ) : (
                <Box sx={{ mt: 1 }}>
                  <OrgDataTable
                    columns={briefingColumns}
                    data={briefingRows}
                    page={briefingsPage}
                    rowsPerPage={briefingsRowsPerPage}
                    totalRows={briefingRows.length}
                    onPageChange={handleBriefingsPageChange}
                    onRowsPerPageChange={handleBriefingsRowsPerPageChange}
                    sortBy="date"
                    sortDirection="desc"
                    disableColumnMenu
                    disableColumnResize
                    enableZebraStripes
                    hideFooter={false}
                    sx={{ mt: 2 }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>
  {/* Contact Add/Edit Dialog */}
      <Dialog open={contactDialogOpen} onClose={() => setContactDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="First name" name="first_name" value={contactForm.first_name} onChange={handleContactFormChange} fullWidth />
            <TextField label="Last name" name="last_name" value={contactForm.last_name} onChange={handleContactFormChange} fullWidth />
            <TextField label="Email" name="email" value={contactForm.email} onChange={handleContactFormChange} fullWidth />
            <TextField label="Department" name="department" value={contactForm.department} onChange={handleContactFormChange} fullWidth />
            <TextField label="Title" name="title" value={contactForm.title} onChange={handleContactFormChange} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveContact}>{editingContact ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
      {/* Delete confirmation dialog */}
      <OrgDialog open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Confirm Delete">
        <Typography>Are you sure you want to delete this contact?</Typography>
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>Delete</Button>
          <Button variant="outlined" onClick={() => setDeleteOpen(false)}>Cancel</Button>
        </Stack>
      </OrgDialog>
    </div>
  );
};

export default SupplierDashboard;
